import React, { useState, useEffect } from 'react';
import { Box, Text, Paper, Button, Alert, Badge, Group } from '@mantine/core';
import { msalInstance } from './AuthProvider';
import { DirectTokenService } from '../services/DirectTokenService';
import { ScimAuthService } from '../services/ScimAuthService';

export const AuthDebugInfo: React.FC = () => {
    const [authState, setAuthState] = useState<{
        hasActiveAccount: boolean;
        activeAccountUsername?: string;
        activeAccountName?: string;
        totalAccounts: number;
        accountUsernames: string[];
        isDirectToken: boolean;
        directTokenUsername?: string;
        authMethod?: string;
    }>({
        hasActiveAccount: false,
        totalAccounts: 0,
        accountUsernames: [],
        isDirectToken: false
    });

    const [showDebug, setShowDebug] = useState(false);

    const updateAuthState = () => {
        // Check for direct token authentication first
        const isDirectToken = DirectTokenService.hasDirectToken();
        const directTokenUsername = DirectTokenService.getDirectTokenUsername();

        // Check MSAL accounts separately
        const msalActiveAccount = msalInstance.getActiveAccount();
        const allAccounts = msalInstance.getAllAccounts();

        // Get the actual current account being used (via ScimAuthService)
        // This works for both MSAL and direct token authentication
        const currentAccount = ScimAuthService.getCurrentAccount();
        const isAuthenticated = ScimAuthService.isAuthenticated();

        // Determine if there's an active account
        // User is authenticated if: direct token OR (MSAL accounts exist)
        const hasActive = isAuthenticated && (currentAccount !== null || allAccounts.length > 0);
        const activeUsername = currentAccount?.username || (allAccounts.length > 0 ? allAccounts[0].username : undefined);
        const activeName = currentAccount?.name || (allAccounts.length > 0 ? allAccounts[0].name : undefined);
        const authMethod = isDirectToken ? 'Direct Token (Debug)' : (allAccounts.length > 0 ? 'MSAL' : 'Unknown');

        setAuthState({
            hasActiveAccount: hasActive,
            activeAccountUsername: activeUsername,
            activeAccountName: activeName,
            totalAccounts: allAccounts.length,
            accountUsernames: allAccounts.map(acc => acc.username),
            isDirectToken: isDirectToken,
            directTokenUsername: directTokenUsername || undefined,
            authMethod: authMethod
        });
    };

    useEffect(() => {
        updateAuthState();

        // Update state when accounts change
        const handleAccountChange = () => {
            updateAuthState();
        };

        // Listen for account changes
        msalInstance.addEventCallback((event) => {
            if (event.eventType === 'msal:loginSuccess' ||
                event.eventType === 'msal:logoutSuccess' ||
                event.eventType === 'msal:acquireTokenSuccess') {
                updateAuthState();
            }
        });

        return () => {
            // Cleanup event listeners if needed
        };
    }, []);

    if (!showDebug) {
        return (
            <Button
                variant="outline"
                size="xs"
                onClick={() => setShowDebug(true)}
                fz="xs"
            >
                Show Auth Debug Info
            </Button>
        );
    }

    return (
        <Paper
            p="md"
            mb="md"
            withBorder
        >
            <Group justify="space-between" align="center" mb="md">
                <Text fw={600} size="lg" c="blue">
                    🔐 Authentication Debug Info
                </Text>
                <Button
                    variant="outline"
                    size="xs"
                    onClick={() => setShowDebug(false)}
                >
                    Hide
                </Button>
            </Group>

            <Box mb="md">
                <Text size="sm" fw={700}>
                    Active Account Status:
                </Text>
                {authState.hasActiveAccount ? (
                    <Alert color="green" mt="xs">
                        <Group gap="sm" align="center" wrap="wrap">
                            <span>✅ Active account: <strong>{authState.activeAccountName || authState.activeAccountUsername}</strong></span>
                            {authState.activeAccountUsername && authState.activeAccountName && authState.activeAccountUsername !== authState.activeAccountName && (
                                <Text size="sm" component="span" c="dimmed">
                                    ({authState.activeAccountUsername})
                                </Text>
                            )}
                            {authState.authMethod && (
                                <Badge
                                    size="sm"
                                    color={authState.isDirectToken ? 'yellow' : 'blue'}
                                >
                                    {authState.authMethod}
                                </Badge>
                            )}
                        </Group>
                    </Alert>
                ) : (
                    <Alert color="yellow" mt="xs">
                        ⚠️ No active account found
                    </Alert>
                )}
            </Box>

            {authState.hasActiveAccount && (
                <Box mb="md">
                    <Text size="sm" fw={700}>
                        Authentication Details:
                    </Text>
                    <Alert color="blue" mt="xs">
                        <Box>
                            <Text size="sm">
                                <strong>Authentication Method:</strong> {authState.authMethod || 'Unknown'}
                            </Text>
                            {authState.isDirectToken ? (
                                <Box mt="xs">
                                    <Text size="sm" c="dimmed">
                                        🔑 Using direct token authentication (localhost/debug mode)
                                    </Text>
                                    {authState.directTokenUsername && (
                                        <Text size="sm" mt={4} c="dimmed">
                                            <strong>Username:</strong> {authState.directTokenUsername}
                                        </Text>
                                    )}
                                </Box>
                            ) : (
                                <Text size="sm" mt={4} c="dimmed">
                                    Using Microsoft Authentication Library (MSAL)
                                </Text>
                            )}
                        </Box>
                    </Alert>
                </Box>
            )}

            <Box mb="md">
                <Text size="sm" fw={700}>
                    Available MSAL Accounts ({authState.totalAccounts}):
                </Text>
                {authState.accountUsernames.length > 0 ? (
                    <Box mt="xs">
                        {authState.accountUsernames.map((username, index) => (
                            <Text key={index} size="sm" ml="md">
                                • {username}
                            </Text>
                        ))}
                    </Box>
                ) : (
                    <Text size="sm" ml="md" c="dimmed">
                        No MSAL accounts available
                        {authState.isDirectToken && (
                            <Text size="xs" component="span" display="block" c="dimmed" fs="italic" mt={4}>
                                (Direct token authentication bypasses MSAL)
                            </Text>
                        )}
                    </Text>
                )}
            </Box>

            <Box>
                <Button
                    size="xs"
                    onClick={updateAuthState}
                    mr="sm"
                >
                    Refresh State
                </Button>
                {!authState.isDirectToken && (
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            msalInstance.clearCache();
                            updateAuthState();
                        }}
                    >
                        Clear Cache
                    </Button>
                )}
                {authState.isDirectToken && (
                    <Button
                        variant="outline"
                        size="xs"
                        color="yellow"
                        onClick={() => {
                            DirectTokenService.clearDirectToken();
                            window.location.reload();
                        }}
                    >
                        Clear Direct Token
                    </Button>
                )}
            </Box>
        </Paper>
    );
};
